import { PaginationFilterType, Uuid } from '@/api/types';
import { Case, DonorType, OrganType } from '@/api/apis/case';
import { PlanType } from '@/api/apis/mission-planner';
import { EmployeeRole } from '@/api/apis/resources';

export enum SuggestionStatus {
  InProgress = 'InProgress',
  Finished = 'Finished',
  Failed = 'Failed',
}

export enum ProcessingStatus {
  None = 'None',
  Ready = 'Ready',
  Running = 'Running',
  Finished = 'Finished',
  Failed = 'Failed',
}

export enum SuggestionOrderByOptions {
  None = 'None',
  InitiatedDate = 'InitiatedDate',
  SuggestedDate = 'SuggestedDate',
  LastReviewDate = 'LastReviewDate',
  UserFullName = 'UserFullName',
}

export enum ResourceWarningEnum {
  CandidateNotFound = 'CANDIDATE_NOT_FOUND',
}

export type SuggestionUserDetailsType = {
  userId: Uuid;
  fullName: string;
  photo: string;
  roles: EmployeeRole[];
};

export type SuggestionLogisticsType = {
  logisticsId: Uuid;
  version: number;
};

export type SuggestionReviewType = {
  id: Uuid;
  rating: number;
  comment: string;
  user: SuggestionUserDetailsType;
  createdAt: string;
  deletedAt: string;
};

export type SuggestionCaseDetails = {
  caseId: Uuid;
  caseName: string;
};

export type SuggestionInfoType = {
  id: Uuid;
  suggestionStatus: SuggestionStatus;
  user: SuggestionUserDetailsType;
  initiatedDate: string;
  suggestedDate: string | null;
  averageRating: number;
};

export type SuggestionTypeResponse = SuggestionInfoType & {
  initialCase: Case;
  targetCases: Case[];
  logistics: SuggestionLogisticsType[];
  planSuggestion: string | null;
  surgeonSuggestion: string | null;
  commercialFlightSuggestion: string | null;
  planStatus: ProcessingStatus;
  surgeonTripsStatus: ProcessingStatus;
  commercialFlightsStatus: ProcessingStatus;
  reviews: SuggestionReviewType[];
  warnings: string[] | null;
};

export type SuggestionType = Omit<SuggestionTypeResponse, 'initialCase' | 'targetCases'> & {
  caseList: Case[];
  chainsCount: number;
};

export type SuggestionSummaryType = SuggestionInfoType & {
  initialCase: SuggestionCaseDetails;
  targetCases: SuggestionCaseDetails[];
  lastReviewDate: string;
  lastReview: string;
};

export type PaginatedSuggestionsListQueryParams = Omit<
  PaginationFilterType<{ search: string }, SuggestionOrderByOptions>,
  'searchQuery'
>;

export type SuggestionCandidatesResponse = {
  initialCases: Case[];
  targetCases: Case[];
};

export type SuggestionReviewRequestBody = Pick<SuggestionReviewType, 'comment' | 'rating'>;

export type MultiSuggestChangeValue<T> = {
  fromValue: T;
  toValue: T;
};

export type MultiSuggestChainItem = {
  caseId: string;
  caseType: DonorType;
  organType: OrganType;
  plan: PlanType;
  orTime: string;
  ocsWarning?: ResourceWarningEnum;
  specialistWarning?: ResourceWarningEnum;
  totalFlightDurationChange: MultiSuggestChangeValue<number>;
};

export type MultiSuggestChain = {
  items: MultiSuggestChainItem[];
  totalFlightDuration: string;
  totalFlightDurationChange: MultiSuggestChangeValue<number>;
};

export type MultiSuggestPlanResponse = {
  chains: MultiSuggestChain[];
  status: string;
  errors: string[];
};

export type MultiSuggestStartBody = {
  initiatedDate: string;
  initialCaseId: string;
  targetCaseIds: string[];
};
